import { useForm, ListButton, SaveButton } from "@refinedev/antd";
import {
  Collapse,
  Form,
  Input,
  InputNumber,
  ColorPicker,
  Typography,
  Flex,
  Upload,
  message,
  Select,
} from "antd";
import { useEffect, useState } from "react";
import type { UploadProps } from "antd";
import { useTranslate } from "@refinedev/core";
import { CheckOutlined, LeftOutlined, PlusOutlined } from "@ant-design/icons";
import { API_BASE_URL } from "@/services/XCONS_ENV";
import { getErrorMessage } from "@/utilities";
import { PhoneNumberInput } from "@/components/phone-number-input";

const { TextArea } = Input;

// El body enviado debe traer SOLO las keys del CreateSellerDto (ValidationPipe forbidNonWhitelisted).
const OPTIONAL_KEYS = [
  "phone",
  "whatsapp",
  "storeHours",
  "regionId",
  "postcode",
  "city",
  "street",
  "latitude",
  "longitude",
  "mainColor",
  "extraInfo",
  "managerEmail",
  "xconsPlan",
  "comissionFee",
  "logo",
  "squareLogo",
] as const;

export const SellersCreate = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps, onFinish } = useForm<any>({
    resource: "sellers",
    action: "create",
    redirect: "list",
    warnWhenUnsavedChanges: true,
    errorNotification: (error: any) => ({
      message: getErrorMessage(error),
      description: t("create.seller.errorTitle", "No se pudo crear el seller"),
      type: "error",
    }),
    successNotification: () => ({
      message: t("create.seller.success", "Seller creado"),
      type: "success",
    }),
  });

  const [regions, setRegions] = useState<{ id: number; name: string }[]>([]);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [squareLogoUrl, setSquareLogoUrl] = useState<string | null>(null);

  const [mapQuery, setMapQuery] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    fetch(`${API_BASE_URL}/sellers/regions`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setRegions(Array.isArray(data) ? data : []))
      .catch(() => setRegions([]));
  }, []);

  const handleOnFinish = (values: any) => {
    const payload: Record<string, any> = {
      code: values.code,
      name: values.name,
      email: values.email,
      legalName: values.legalName,
      taxvat: values.taxvat,
    };
    OPTIONAL_KEYS.forEach((key) => {
      let v = values[key];
      if (key === "mainColor" && v && typeof v !== "string" && v.toHexString) {
        v = v.toHexString();
      }
      if (v !== undefined && v !== null && v !== "") payload[key] = v;
    });
    return onFinish(payload);
  };

  const handleSaveClick = () => {
    formProps.form
      ?.validateFields()
      .then((values) => handleOnFinish(values))
      .catch(() => {});
  };

  const makeLogoUpload = (
    attribute: "logo" | "square_logo",
    fieldName: "logo" | "squareLogo",
    setUrl: (url: string | null) => void,
  ): UploadProps => ({
    accept: "image/*",
    maxCount: 1,
    listType: "picture-card" as const,
    showUploadList: false,
    customRequest: async ({ file, onSuccess, onError }) => {
      const fd = new FormData();
      fd.append("file", file as Blob);
      fd.append("attribute", attribute);
      try {
        const token = localStorage.getItem("access_token");
        const res = await fetch(`${API_BASE_URL}/sellers/logo`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: fd,
        });
        if (!res.ok) {
          const raw = await res.text();
          let detail = raw;
          try {
            const parsed = JSON.parse(raw);
            const msg = parsed?.message ?? parsed?.error;
            detail = Array.isArray(msg) ? msg.join(" · ") : (msg ?? raw);
          } catch {
            // body no-JSON → usar texto tal cual
          }
          throw new Error(detail || `Error ${res.status}`);
        }
        const data = await res.json();
        // Al form va el path relativo, que es lo que persiste la columna; al
        // preview, la URL absoluta que arma el backend con la base de media.
        formProps.form?.setFieldValue(fieldName, data.path);
        setUrl(data.url ?? data.path);
        message.success("Imagen subida");
        onSuccess?.(data);
      } catch (e: any) {
        message.error(`No se pudo subir la imagen: ${getErrorMessage(e)}`);
        onError?.(e);
      }
    },
  });

  const collapseItems = [
    {
      key: "technical",
      label: <span style={{ fontWeight: 600 }}>Técnico</span>,
      styles: { header: { height: 40, alignItems: "center" } },
      children: (
        <Form
          {...formProps}
          layout="horizontal"
          labelCol={{ span: 9 }}
          wrapperCol={{ span: 15 }}
          style={{ maxWidth: 640 }}
          initialValues={{ mainColor: "#1677FF" }}
          colon
        >
          <Form.Item
            label="Código"
            name="code"
            rules={[
              { required: true },
              { pattern: /^[A-Za-z0-9_-]+$/, message: "Solo letras, números, guion y guion bajo" },
            ]}
          >
            <Input placeholder="sellercode" />
          </Form.Item>
          <Form.Item label="Nombre del seller" name="name" rules={[{ required: true }]}>
            <Input placeholder="companyname" />
          </Form.Item>
          <Form.Item label="Color principal" name="mainColor" rules={[{ required: true }]}>
            <ColorPicker showText format="hex" />
          </Form.Item>
          <Form.Item
            label="Logo del seller"
            extra="PNG o Webp transparente en tamaño 200x700px"
          >
            <Form.Item name="logo" noStyle>
              <Input style={{ display: "none" }} />
            </Form.Item>
            <Upload {...makeLogoUpload("logo", "logo", setLogoUrl)}>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="logo"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Subir</div>
                </div>
              )}
            </Upload>
          </Form.Item>
          <Form.Item
            label="Logo cuadrado del seller"
            extra="PNG o Webp transparente en tamaño 200x200px"
          >
            <Form.Item name="squareLogo" noStyle>
              <Input style={{ display: "none" }} />
            </Form.Item>
            <Upload {...makeLogoUpload("square_logo", "squareLogo", setSquareLogoUrl)}>
              {squareLogoUrl ? (
                <img
                  src={squareLogoUrl}
                  alt="logo cuadrado"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Subir</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: "company",
      label: <span style={{ fontWeight: 600 }}>Información de empresa</span>,
      styles: { header: { height: 40, alignItems: "center" } },
      children: (
        <Form
          {...formProps}
          layout="horizontal"
          labelCol={{ span: 9 }}
          wrapperCol={{ span: 15 }}
          style={{ maxWidth: 640 }}
          colon
        >
          <Form.Item
            label="Nombre de empresa"
            name="legalName"
            rules={[{ required: true }]}
          >
            <Input placeholder="Nombre o razón social de tu empresa" />
          </Form.Item>
          <Form.Item label="CUIT / Tax ID" name="taxvat" rules={[{ required: true }]}>
            <Input placeholder="CUIT o número de identificación fiscal" />
          </Form.Item>
          <Form.Item
            label="WhatsApp de ventas"
            name="whatsapp"
            rules={[{ required: true }]}
            extra="Se usa como contacto público por defecto del Seller"
          >
            <PhoneNumberInput placeholder="(Cód. Área) - Teléfono" />
          </Form.Item>
          <Form.Item
            label="Teléfono de ventas"
            name="phone"
            extra="Se usa como contacto público por defecto del Seller"
          >
            <PhoneNumberInput placeholder="(Cód. Área) - Teléfono" />
          </Form.Item>
          <Form.Item
            label="Email de ventas"
            name="email"
            rules={[{ required: true, type: "email" }]}
            extra="Se usa como contacto público por defecto del Seller"
          >
            <Input placeholder="ventas@nombreempresa.com" />
          </Form.Item>
          <Form.Item
            label="Email del manager"
            name="managerEmail"
            rules={[{ required: true, type: "email" }]}
            extra="Generalmente el CTO o dueño de la empresa. Esta cuenta tendrá todos los permisos de administrador del Seller, sus operadores, etc."
          >
            <Input placeholder="propietario@nombreempresa.com" />
          </Form.Item>
        </Form>
      ),
    },
    {
      key: "pickup",
      label: <span style={{ fontWeight: 600 }}>Punto de retiro por defecto</span>,
      styles: { header: { height: 40, alignItems: "center" } },
      children: (
        <Form
          {...formProps}
          layout="horizontal"
          labelCol={{ span: 9 }}
          wrapperCol={{ span: 15 }}
          style={{ maxWidth: 640 }}
          colon
        >
          <Form.Item
            label="Dirección"
            name="street"
            rules={[{ required: true }]}
            extra="Usá la dirección del punto de retiro tal como aparece en Google Maps"
          >
            <Input
              placeholder="Ej: Av. Juan B Justo 1250"
              onChange={(e) => setMapQuery(e.target.value)}
            />
          </Form.Item>
          {mapQuery && (
            <Form.Item wrapperCol={{ offset: 9, span: 15 }}>
              <iframe
                key={mapQuery}
                src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                width="100%"
                height="200"
                style={{ border: 0, borderRadius: 8 }}
                loading="lazy"
                title="Mapa del punto de retiro"
              />
            </Form.Item>
          )}
          <Form.Item label="Ciudad" name="regionId" rules={[{ required: true }]}>
            <Select
              showSearch
              allowClear
              placeholder="Seleccionar ciudad"
              optionFilterProp="label"
              options={regions.map((r) => ({ value: r.id, label: r.name }))}
            />
          </Form.Item>
          <Form.Item label="Código postal" name="postcode" rules={[{ required: true }]}>
            <InputNumber placeholder="5000" controls={false} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Latitud"
            name="latitude"
            extra="El sistema usa latitud y longitud para calcular distancias"
          >
            <InputNumber placeholder="-64.123456" controls={false} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Longitud" name="longitude">
            <InputNumber placeholder="-31.1441273" controls={false} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Horarios" name="storeHours">
            <Input placeholder="Lun a Vie 8am a 5pm" />
          </Form.Item>
          <Form.Item
            label="Texto adicional"
            name="extraInfo"
            extra="Información extra que puede ayudar a ubicar el punto de retiro"
          >
            <TextArea placeholder='"Junto al Wallmart supercenter"' rows={3} />
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <Flex vertical style={{ height: "100%", minHeight: 0 }}>
      <Flex align="center" style={{ marginBottom: 24, flexShrink: 0 }}>
        <ListButton hideText icon={<LeftOutlined />} />
        <Typography.Title level={3} style={{ marginBottom: 0, marginLeft: 10 }}>
          Crear nuevo seller
        </Typography.Title>
      </Flex>

      <div style={{ flex: 1, overflowY: "auto" }}>
        <Collapse
          defaultActiveKey={["technical", "company", "pickup"]}
          items={collapseItems}
        />
      </div>

      <div style={{ paddingTop: 32, paddingBottom: 16, flexShrink: 0 }}>
        <Flex justify="space-between">
          <ListButton icon={<LeftOutlined />}>Volver</ListButton>
          <SaveButton
            {...saveButtonProps}
            onClick={handleSaveClick}
            icon={<CheckOutlined />}
            iconPosition="end"
          >
            Crear y guardar
          </SaveButton>
        </Flex>
      </div>
    </Flex>
  );
};
